import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { REIButton, REIDialoge, REIDropDown, REIInputNumber, REIInputText } from "../../../../components/ui";
import { RadioButton } from "primereact/radiobutton";
import { ILoan } from "../../../../interface/addAnalyser";
import DatePicker from "../../../../components/ui/calender";
import { Slider } from "primereact/slider";
import { GET_LENGTH_OPTIONS } from "../../../../utils/constants";
import {
  getDateStringWithGivenFormat,
  getAdjustedFormattedDate,
  isPropertyDetailValid,
} from "../../../../utils/commonUtil";
import { useAnalysedPropertyContext } from "./AnalysedPropertyContext";
import { Calendar } from "primereact/calendar";
interface ILoanDetailsTabProps {
  loan: ILoan;
  updateLoanValue?: (loan: ILoan) => void;
  setValues?: (name: string, value: string | number, loan: ILoan) => void;
  movetoPortfolio?: boolean;
  purchaseDate?: string;
  onDateValidation?: (validationType: "loanDate" | "nextReviewDate", isValid: boolean) => void;
  dateErrors?: { loanDate: boolean; nextReviewDate: boolean };
  isActive?: boolean;
  loanAmount?: number;
}

export interface LoanValidation {
  validate: () => boolean;
}

const LoanDetailsTab = forwardRef<LoanValidation, ILoanDetailsTabProps>(
  (
    {
    loan,
    updateLoanValue,
    setValues,
    movetoPortfolio,
    onDateValidation,
    dateErrors,
    purchaseDate,
    isActive,
    loanAmount,
    },
    ref
  ) => {
    const { analysedProperty } = useAnalysedPropertyContext();
    const [loans, setLoans] = useState<ILoan>(loan);
    const [reset, setReset] = useState<boolean>(false);
    const [tempValues, setTempValues] = useState<any>();
    const [proceeded, setProceed] = useState<boolean>(false);
    const [isPrincipal, setIsPrincipal] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [errors, setErrors] = useState({
      interestRate: false,
      loanLengthMonth: false,
      loanLengthYear: false,
      loanDate: false,
      loanAmount: false,
    });

    const getYearOptions = () => [...GET_LENGTH_OPTIONS(0, 99, undefined, 2, "desc")];

    const getMonthOptions = () => [...GET_LENGTH_OPTIONS(0, 11, undefined, 2, "desc")];

    useEffect(() => {
      if (!reset) {
        setLoans(loan);
      }
    }, [loan, reset]);

    useEffect(() => {
      setIsPrincipal(loan.IsPrincipal === 1);
    }, [loan.IsPrincipal]);

    useEffect(() => {
      if (updateLoanValue && loans && loans.isFromPortfolio) {
        updateLoanValue(loans);
      }
    }, [loans, updateLoanValue]);

    useEffect(() => {
      if (isActive) {
        validateField("InterestRate", loans.InterestRate);
        validateField("Length_Years", loans.Length_Years);
        validateField("Amount", loans.Amount);
      }
    }, [isActive]);

    useEffect(() => {
      if (loanAmount && loanAmount > 0) {
        setLoanValues("Amount", loanAmount, loans);
      }
    }, [loanAmount]);

    const validationRules = {
      InterestRate: (value?: number) => (value !== undefined && value > 0 ? true : false),
      Amount: (value?: number) => (value !== undefined && value > 0 ? true : false),
      Length_Years: (value?: number) =>
        value !== undefined && value > getYearOptions().find((opt) => opt.value === 1)?.value!,
      LoanDate: (date?: string, purchaseDate?: string) => {
        if (!date) return false;
        const loanDateObj = new Date(date);
        const today = new Date();
        const purchaseDateObj = purchaseDate ? new Date(purchaseDate) : today;
        return loanDateObj <= today && loanDateObj >= purchaseDateObj;
      },
    };

    const validateField = (name: keyof typeof validationRules, value?: number, purchaseDate?: string) => {
      let isValid;
      let errorKey = "";
      switch (name) {
        case "InterestRate":
          errorKey = "interestRate";
          break;
        case "Length_Years":
          errorKey = "loanLengthYear";
          break;
        case "LoanDate":
          errorKey = "loanDate";
          break;
        case "Amount":
          errorKey = "loanAmount";
          break;
        default:
          errorKey = name;
      }
      if (name === "LoanDate") {
        isValid = validationRules.LoanDate(value !== undefined ? String(value) : undefined, purchaseDate);
      } else {
        isValid = validationRules[name](value as number);
      }
      setErrors((prev) => ({ ...prev, [errorKey]: !isValid }));
      return isValid;
    };

    useImperativeHandle(ref, () => ({
      validate: () => {
        const interestRateValid = validateField("InterestRate", loans.InterestRate);
        const yearLengthValid = validateField("Length_Years", loans.Length_Years);
        const amountValid = validateField("Amount", loans.Amount);
        return interestRateValid && yearLengthValid && amountValid;
      },
    }));

    const validateAndResetErrors = (name: string, value: number | undefined) => {
      if (name in validationRules) {
        validateField(name as keyof typeof validationRules, value);
      }
    };

    const hideConfirmationDialogue = () => {
      setShowConfirmation(false);
    };

    const proceedWithUpdate = (name: string, value: string | number, loans: ILoan, shouldValidate: boolean = false) => {
      setReset(true);
      setProceed(true);
      setLoans((prevState) => {
        const updatedLoan = {
          ...prevState,
          [name]: value !== undefined ? value : 0,
        };
        return updatedLoan;
      });
      setTimeout(() => {
        setReset(false);
        setValues?.(name, value, loans);
        if (
          shouldValidate &&
          (name === "Length_Years" || name === "Length_Months" || name === "InterestRate" || name === "Amount")
        ) {
          validateAndResetErrors(name, value as number);
        }
      }, 10);
    };

    const setLoanValues = (name: keyof ILoan, value: string | number, loans: ILoan) => {
      if (loans && loans.isFromPortfolio && name !== "Name" && name !== "RefCode" && !proceeded) {
        name !== "IsPrincipal" && setShowConfirmation(true);
        setTempValues({ name, value: value, loans });
        return;
      }
      if (name === "Length_Years" || name === "Length_Months" || name === "InterestRate" || name === "Amount") {
        validateAndResetErrors(name, value);
      }
      proceedWithUpdate(name, value, loans);
    };

    const renderSliderWithInput = (
      label: string,
      value: number,
      name: string,
      max: number,
      min: number = 0,
      isPercentage = false
    ) => (
      <div className="flex w-full py-1">
        <div className="w-[20%] mt-2">{label}</div>
        <div className="w-[40%] pr-2">
          <Slider
            id={`ldtab-sldr-${name.toLowerCase()}`}
            data-testid={`ldtab-sldr-${name.toLowerCase()}`}
            key={name}
            value={value > max ? max : value}
            onChange={(e) => {
              setLoanValues(name, e.value as number, loans);
            }}
            className="w-full mt-4 cursor-pointer"
            min={min}
            max={max}
          />
        </div>
        <div className="w-[40%] flex flex-col pr-1">
          <REIInputNumber
            id={`ldtab-num-${name.toLowerCase()}`}
            value={value}
            onValueChange={(e) => setLoanValues(name, e.target.value as number, loans)}
            className={`w-full ${errors.interestRate && name === "InterestRate" ? "p-invalid" : ""}`}
            useGrouping={true}
            min={min}
            max={isPercentage ? max : undefined}
            maxFractionDigits={2}
            inputName={name}
          />
          {errors.interestRate && name === "InterestRate" && (
            <span className="text-red-500 text-sm mt-1">Interest rate must be greater than 0</span>
          )}
        </div>
      </div>
    );

    const handleIsPrincipalChange = (value: boolean) => {
      if (loans.isFromPortfolio && !proceeded) {
        setIsPrincipal(value);
        setShowConfirmation(true);
        return;
      }
      setIsPrincipal(value);
      setLoanValues("IsPrincipal", value ? 1 : 0, loans);
    };

    return (
      <>
        <REIDialoge
          showHeader={false}
          header=""
          visible={showConfirmation && !proceeded}
          handleEditClick={hideConfirmationDialogue}
          position="center"
          style={{ width: "60vw" }}
          customClass="edit-default-profile"
          contentComponent={
            <div className="flex flex-col items-start p-4 w-full">
              <p className="mb-4">
                Changing loan details will update your previous forecast. Are you sure you want to proceed?
              </p>
              <div className="flex justify-between w-full items-center">
                <a
                  id="ldtab-lnk-cancelchange"
                  data-testid="ldtab-lnk-cancelchange"
                  className="text-blue-500 hover:text-blue-700 cursor-pointer"
                  onClick={() => {
                    if (tempValues && tempValues.name !== "IsPrincipal") {
                      proceedWithUpdate(tempValues.name, 0, loans);
                      setTimeout(() => {
                        proceedWithUpdate(tempValues.name, loans[tempValues.name as keyof ILoan] as any, loans);
                        setProceed(false);
                        hideConfirmationDialogue();
                      }, 500);
                    } else {
                      setIsPrincipal(!isPrincipal);
                      hideConfirmationDialogue();
                    }
                  }}
                >
                  Cancel the change to the loan details
                </a>

                <REIButton
                  id="ldtab-btn-proceed"
                  label="Proceed and update the previous forecast"
                  onClick={() => {
                    proceedWithUpdate(tempValues.name, tempValues.value, tempValues.loans, true);
                    hideConfirmationDialogue();
                  }}
                  classNames="ml-auto"
                ></REIButton>
              </div>
            </div>
          }
        ></REIDialoge>
        {loans && (
          <div>
            <div className="flex w-full items-center mb-2 space-x-4 gap-2">
              <div className="flex w-1/2 items-center">
                <div className="w-[20%] mt-2">Lender/name of loan</div>
                <div className="w-[80%] flex">
                  <REIInputText
                    id="ldtab-txt-loanname"
                    name="loanName"
                    value={loans?.Name ?? ""}
                    onChange={(e) => setLoanValues("Name", e.target.value, loans)}
                    type="text"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="flex w-1/2 items-center space-x-4">
                <div className="flex items-center">
                  <RadioButton
                    inputId="interestOnly"
                    data-testid="ldtab-rdo-interestonly"
                    name="loanType"
                    value={false}
                    onChange={() => handleIsPrincipalChange(false)}
                    checked={isPrincipal === false}
                  />
                  <label htmlFor="interestOnly" className="ml-2">
                    Interest only
                  </label>
                </div>
                <div className="flex items-center">
                  <RadioButton
                    inputId="principalAndInterest"
                    data-testid="ldtab-rdo-principalinterest"
                    name="loanType"
                    value={true}
                    onChange={() => handleIsPrincipalChange(true)}
                    checked={isPrincipal === true}
                  />
                  <label htmlFor="principalAndInterest" className="ml-2">
                    Principal & interest
                  </label>
                </div>
              </div>
            </div>

            {movetoPortfolio && (
              <div className="flex w-full items-center mb-2 space-x-4 gap-2">
                <div className="flex w-1/2 items-center">
                  <div className="w-[20%] mt-2">Loan reference</div>
                  <div className="w-[80%] flex">
                    <REIInputText
                      id="ldtab-txt-refcode"
                      name="refCode"
                      value={loans?.RefCode}
                      onChange={(e) => setLoanValues("RefCode", e.target.value, loans)}
                      type="text"
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="flex w-1/2 items-center"></div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 self-center">
              {!loans?.isFromPortfolio && (
                <div className={`flex flex-col w-full self-center items-center ${errors.loanAmount ? "" : " p-1"}`}>
                  <div className="flex w-full py-1">
                    <div className="w-[20%] mt-2">Amount of loan ($)</div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="ldtab-sldr-amount"
                        data-testid="ldtab-sldr-amount"
                        key="Amount"
                        value={loans.Amount > 500000 ? 500000 : loans.Amount}
                        onChange={(e) => {
                          setLoanValues("Amount", e.value as number, loans);
                        }}
                        className="w-full mt-4 cursor-pointer"
                        min={0}
                        max={500000}
                      />
                    </div>
                    <div className="w-[40%] flex pr-1 h-fit">
                      <REIInputNumber
                        id="ldtab-num-amount"
                        value={loans.Amount}
                        onValueChange={(e) => setLoanValues("Amount", e.target.value as number, loans)}
                        className={`w-full ${errors.loanAmount ? "p-invalid" : ""}`}
                        useGrouping={true}
                        min={0}
                        maxFractionDigits={2}
                        inputName="Amount"
                      />
                    </div>
                  </div>
                  <div className="item-center self-end">
                    {errors.loanAmount && (
                      <span className="text-red-500 text-sm mt-1">Loan amount must be greater than 0</span>
                    )}
                  </div>
                </div>
              )}
              {loans?.isFromPortfolio && (
                <div className={`flex flex-col w-full self-center items-center ${errors.loanAmount ? "" : " p-1"}`}>
                  <div className="flex w-full py-1">
                    <div className="w-[20%] mt-2 mr-1">Loan Balance ($)* </div>
                    <div className="w-[40%] pr-2">
                      <Slider
                        id="ldtab-sldr-balance"
                        data-testid="ldtab-sldr-balance"
                        key="Balance"
                        value={loans.Amount > 500000 ? 500000 : loans.Amount}
                        onChange={(e) => {
                          setLoanValues("Amount", e.value as number, loans);
                        }}
                        className="w-full mt-4 cursor-pointer"
                        min={0}
                        max={500000}
                        step={1000}
                      />
                    </div>
                    <div>
                      <div className="w-[40%] flex pr-1">
                        <REIInputNumber
                          id="ldtab-num-balance"
                          value={loans.Amount}
                          onValueChange={(e) => setLoanValues("Amount", e.target.value as number, loans)}
                          className={`w-full ${errors.loanAmount ? "p-invalid" : ""}`}
                          useGrouping={true}
                          min={0}
                          maxFractionDigits={2}
                          inputName="Amount"
                        />
                      </div>
                      <div className="flex item-center self-end">
                        {errors.loanAmount && (
                          <span className="text-red-500 text-sm mt-1">Loan amount must be greater than 0</span>
                        )}
                      </div>{" "}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex w-full items-center p-1">
                <div className="w-[30%] ">
                  Loan length<span>{isPropertyDetailValid(analysedProperty) ? "" : "*"}</span>
                </div>
                <div className="flex w-[60%] items-center space-x-2">
                  <div className="flex flex-col w-full">
                    <div className="flex items-center space-x-2">
                      <REIDropDown
                        id="ldtab-drop-lengthyears"
                        data={getYearOptions()}
                        label="Label"
                        value={loans?.Length_Years}
                        optionValue="value"
                        onSelectionChange={(e) => {
                          setLoanValues("Length_Years", e.value, loans);
                        }}
                        className={`w-full ${errors.loanLengthYear ? "p-invalid" : ""}`}
                      />
                      <span>yrs</span>
                      <REIDropDown
                        id="ldtab-drop-lengthmonths"
                        data={getMonthOptions()}
                        label="Label"
                        optionValue="value"
                        value={loans?.Length_Months}
                        onSelectionChange={(e) => {
                          setLoanValues("Length_Months", e.value, loans);
                        }}
                        className={`w-full ${errors.loanLengthMonth ? "p-invalid" : ""}`}
                      />
                      <span>months</span>
                    </div>
                    {errors.loanLengthYear && (
                      <span className="text-red-500 text-sm mt-1">Loan length must be greater than 0 years</span>
                    )}
                  </div>
                </div>
              </div>
              {renderSliderWithInput(
                `Interest rate (%)${isPropertyDetailValid(analysedProperty) ? "" : "*"}`,
                loans?.InterestRate,
                "InterestRate",
                100,
                0,
                true
              )}
              {loans.isFromPortfolio && (
                <>
                  <div className="flex w-full items-center p-1">
                    <div className="w-[30%]">Fixed interest period (yrs)</div>
                    <div className="flex w-[60%] items-center space-x-2">
                      <REIDropDown
                        id="ldtab-drop-fixedyears"
                        data={getYearOptions()}
                        label="Label"
                        value={loans?.FixedInterestPeriod_Years ? loans?.FixedInterestPeriod_Years : 0}
                        optionValue="value"
                        onSelectionChange={(e) => setLoanValues("FixedInterestPeriod_Years", e.value, loans)}
                      />
                      <span>yrs</span>
                      <REIDropDown
                        id="ldtab-drop-fixedmonths"
                        data={getMonthOptions()}
                        label="Label"
                        optionValue="value"
                        value={loans?.FixedInterestPeriod_Months ? loans?.FixedInterestPeriod_Months : 0}
                        onSelectionChange={(e) => setLoanValues("FixedInterestPeriod_Months", e.value, loans)}
                      />
                      <span>months</span>
                    </div>
                  </div>
                  <div className="flex w-full items-center p-1 gap-2">
                    <div className="w-[20%]">Loan date</div>
                    <div className="w-[80%] flex">
                      {!loans.isFromPortfolio && (
                        <DatePicker
                          dateString={
                            loans.Date != ""
                              ? getDateStringWithGivenFormat(new Date(loans.Date))
                              : getDateStringWithGivenFormat(new Date())
                          }
                          onChange={(date) => setLoanValues("Date", getDateStringWithGivenFormat(date.value), loans)}
                          dateFormat="dd/mm/yy"
                          className="w-full"
                        ></DatePicker>
                      )}

                      {loans.isFromPortfolio && (
                        <div>
                          <Calendar
                            value={loans.Date != "" ? new Date(loans.Date) : new Date(Date.now())}
                            showIcon={true}
                            onChange={(e) => {
                              if (e.value) {
                                const selectedDate = e.value as Date;
                                const dateValue = getDateStringWithGivenFormat(selectedDate);
                                setLoanValues("Date", dateValue, loans);

                                if (purchaseDate) {
                                  const purchaseDateObj = new Date(purchaseDate);
                                  const today = new Date();

                                  const isValid = selectedDate >= purchaseDateObj && selectedDate <= today;
                                  onDateValidation?.("loanDate", isValid);
                                }
                              }
                            }}
                            dateFormat="dd/mm/yy"
                            className={`w-full ${dateErrors?.loanDate ? "p-invalid" : ""}`}
                            mask="99/99/9999"
                            readOnlyInput={false}
                            showOnFocus={false}
                            hideOnDateTimeSelect={true}
                          ></Calendar>
                          <div className="h-5 overflow-hidden" hidden={!dateErrors?.loanDate}>
                            <small className="text-red-500 block mt-1">
                              Loan date must be between purchase date and today
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex w-full items-center p-1">
                    <div className="w-[30%]">Next rate review date</div>
                    <div className="flex w-[60%] items-center space-x-2">
                      {!loans.isFromPortfolio && (
                        <DatePicker
                          dateString={
                            loans.InterestRateReviewDate != ""
                              ? getDateStringWithGivenFormat(new Date(loans.InterestRateReviewDate))
                              : getAdjustedFormattedDate(new Date(Date.now()), { years: 3 })
                          }
                          onChange={(date) =>
                            setLoanValues("InterestRateReviewDate", getDateStringWithGivenFormat(date.value), loans)
                          }
                          dateFormat="dd/mm/yy"
                        ></DatePicker>
                      )}
                      {loans.isFromPortfolio && (
                        <div>
                          <Calendar
                            value={
                              loans.InterestRateReviewDate != ""
                                ? new Date(loans.InterestRateReviewDate)
                                : new Date(getAdjustedFormattedDate(new Date(), { years: 3 }))
                            }
                            onChange={(e) => {
                              if (e.value) {
                                const dateValue = e.value ? getDateStringWithGivenFormat(e.value) : "";
                                setLoanValues("InterestRateReviewDate", dateValue, loans);
                                const selectedDate = e.value as Date;
                                const startDate = new Date(loans.Date);
                                if (startDate) {
                                  const isValid = selectedDate >= startDate;
                                  onDateValidation?.("nextReviewDate", isValid);
                                }
                              }
                            }}
                            dateFormat="dd/mm/yy"
                            showIcon={true}
                            className="w-full"
                            mask="99/99/9999"
                            readOnlyInput={false}
                            showOnFocus={false}
                            hideOnDateTimeSelect={true}
                          />
                          <div className="h-5 overflow-hidden" hidden={!dateErrors?.nextReviewDate}>
                            <small className="text-red-500 block mt-1">Next review date must be after loan date</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              {!loans.isFromPortfolio && (
                <>
                  {renderSliderWithInput("Establishment fees ($)", loans.EstablishmentFees, "EstablishmentFees", 50000)}

                  {renderSliderWithInput("Other loan costs ($)", loans.OtherLoanCosts, "OtherLoanCosts", 50000)}

                  {renderSliderWithInput("Valuation fees ($)", loans.ValuationFees, "ValuationFees", 50000)}
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
);

export default LoanDetailsTab;
